import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { InterventionGroupDto, TreatmentPlanDto } from '../../api/generated';
import { flattenGroups } from '../../utils/ganttUtils';
import type { AffectedGroupsChoice } from './affectedGroups';
import styles from './AffectedGroupsPicker.module.css';

interface AffectedGroupsPickerProps {
  plan: TreatmentPlanDto;
  selectedGroup: InterventionGroupDto;
  // The selection lives with the caller, so the radio can only ever show a
  // scope the caller is already acting on. Holding it here instead let the
  // pre-selected scope go unannounced: a radio that is already checked fires
  // no change event, so the caller kept whatever it had seeded itself with.
  choice: AffectedGroupsChoice;
  onChange: (choice: AffectedGroupsChoice) => void;
}

export const AffectedGroupsPicker = ({
  plan,
  selectedGroup,
  choice,
  onChange
}: AffectedGroupsPickerProps) => {
  const { t } = useTranslation();
  const groupName = useId();
  const flatSelected = flattenGroups(plan).find((g) => g.id === selectedGroup.id);
  const weekLabel = flatSelected ? `${t('week-label')} ${String(flatSelected.weekNumber)}` : '';

  const options: { value: AffectedGroupsChoice; label: string }[] = [
    { value: 'plan', label: t('affected-groups-all') },
    {
      value: 'onwards',
      label: `${t('affected-groups-onwards')} (${t('affected-groups-from')} ${weekLabel})`
    },
    { value: 'group', label: `${t('affected-groups-this')} (${weekLabel})` }
  ];

  return (
    <div className={styles.picker}>
      <span className={styles.label}>{t('affected-groups-apply-to')}</span>
      <div className={styles.options}>
        {options.map((opt) => (
          <label key={opt.value} className={styles.option}>
            <input
              type="radio"
              name={groupName}
              value={opt.value}
              checked={choice === opt.value}
              onChange={() => onChange(opt.value)}
              className={styles.radio}
            />
            <span className={styles.optionLabel}>{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};
